<div
  class="tw-flex tw-flex-col tw-gap-3"
  appClickOutside
  appClickOutsideFocusOut
  (clickOutside)="onClickOutside($event)"
>
  <!-- header: label + add-new (desktop unchanged); on sm (mobile) the chip breaks onto its own row above -->
  <div class="tw-flex tw-flex-col tw-gap-2">
    @if (selectedName()) {
      <!-- mobile-only: chip on its own row -->
      <div class="tw-hidden sm:tw-block">
        <span
          class="tw-inline-flex tw-min-w-0 tw-max-w-[140px] tw-items-center tw-gap-1 tw-rounded-full tw-bg-dt-surface-on-action tw-pl-3 tw-pr-2 tw-py-1 tw-text-[13px] tw-font-medium tw-text-dt-slate-0"
        >
          <span class="tw-min-w-0 tw-truncate" [title]="selectedName()">{{ selectedName() }}</span>
          <icon
            class="tw-shrink-0 tw-cursor-pointer"
            [name]="'close'"
            [size]="5"
            [currentColor]="'white'"
            (click)="categoryRemoved.emit()"
          ></icon>
        </span>
      </div>
    }
    <div class="tw-flex tw-items-center tw-justify-between">
      <div class="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
        <span class="tw-shrink-0 tw-text-[13px] tw-font-bold tw-text-dt-text-tertiary">{{
          "selectCategory" | i18n
        }}</span>
        @if (selectedName()) {
          <!-- desktop-only: chip inline next to the label -->
          <span
            class="sm:tw-hidden tw-inline-flex tw-min-w-0 tw-max-w-[140px] tw-items-center tw-gap-1 tw-rounded-full tw-bg-dt-surface-on-action tw-pl-3 tw-pr-2 tw-py-1 tw-text-[13px] tw-font-medium tw-text-dt-slate-0"
          >
            <span class="tw-min-w-0 tw-truncate" [title]="selectedName()">{{
              selectedName()
            }}</span>
            <icon
              class="tw-shrink-0 tw-cursor-pointer"
              [name]="'close'"
              [size]="5"
              [currentColor]="'white'"
              (click)="categoryRemoved.emit()"
            ></icon>
          </span>
        }
      </div>
      <button
        type="button"
        class="tw-shrink-0 tw-cursor-pointer tw-whitespace-nowrap tw-border-0 tw-bg-transparent tw-text-[13px] tw-font-medium tw-text-dt-text-action"
        (click)="creating() ? cancelCreate() : startCreate()"
      >
        {{ (creating() ? "backToSearch" : "addNewCategory") | i18n }}
      </button>
    </div>
  </div>

  <!-- inset combobox -->
  <div
    class="tw-flex tw-flex-col tw-gap-2 tw-rounded-xl tw-bg-dt-surface-card tw-p-2 tw-shadow-neuomorphic-inset"
  >
    @if (creating()) {
      <!-- create-new row -->
      <div class="tw-flex tw-h-[48px] tw-items-center tw-gap-2 tw-px-2">
        <span class="tw-text-[28px] tw-leading-none tw-text-dt-text-action">+</span>
        <input
          type="text"
          class="tw-h-full tw-min-w-0 tw-flex-1 tw-border-none tw-bg-transparent tw-text-[14px] tw-outline-none"
          [placeholder]="'categoryName' | i18n"
          [value]="newCategoryName"
          (input)="onNewNameInput($event)"
          (keydown.enter)="confirmCreate()"
        />
        <button
          type="button"
          class="tw-shrink-0 tw-cursor-pointer tw-whitespace-nowrap tw-border-0 tw-bg-transparent tw-text-[13px] tw-font-medium tw-text-danger"
          (click)="cancelCreate()"
        >
          {{ "cancel" | i18n }}
        </button>
        <button
          type="button"
          class="tw-shrink-0 tw-cursor-pointer tw-whitespace-nowrap tw-rounded-lg tw-border-0 tw-bg-dt-surface-card tw-px-3 tw-py-1 tw-text-[13px] tw-font-medium tw-text-dt-text-headings tw-shadow-button hover:tw-shadow-hover active:tw-shadow-inner"
          (click)="confirmCreate()"
        >
          {{ "add" | i18n }}
        </button>
      </div>
    } @else {
      <!-- search row (trigger) -->
      <div
        class="tw-flex tw-h-[48px] tw-items-center tw-gap-2 tw-px-2 tw-cursor-text"
        (click)="expand()"
      >
        <icon
          class="tw-shrink-0"
          [name]="'categories'"
          [size]="5"
          [currentColor]="'dt-icon-disabled'"
        ></icon>
        <input
          type="text"
          role="combobox"
          aria-autocomplete="list"
          aria-haspopup="listbox"
          aria-controls="categorySelectListbox"
          [attr.aria-expanded]="expanded()"
          [attr.aria-label]="'selectCategory' | i18n"
          class="tw-h-full tw-flex-1 tw-border-none tw-bg-transparent tw-text-[14px] tw-outline-none"
          [placeholder]="'searchForACategory' | i18n"
          [value]="searchTerm()"
          (input)="onSearchInput($event)"
          (focus)="expand()"
        />
        @if (searchTerm()) {
          <icon
            class="tw-cursor-pointer"
            [name]="'close'"
            [size]="5"
            [currentColor]="'dt-text-secondary'"
            (click)="$event.stopPropagation(); searchTerm.set('')"
          ></icon>
        }
        <div class="tw-h-5 tw-w-0.5 tw-flex-shrink-0 tw-bg-neutral-800"></div>
        <icon
          class="tw-cursor-pointer tw-transition-transform"
          [name]="'down-arrow'"
          [size]="5"
          [currentColor]="'neutral-800'"
          [disableArrowOverride]="true"
          [ngClass]="{ 'tw-rotate-180': expanded() }"
          (click)="$event.stopPropagation(); toggleExpand()"
        ></icon>
      </div>
    }

    <!-- expanded list of category cards -->
    @if (expanded() || creating()) {
      <div
        appGlossScroll
        id="categorySelectListbox"
        role="listbox"
        class="tw-flex tw-max-h-[240px] tw-flex-col tw-gap-2 tw-overflow-y-auto tw-overscroll-contain tw-pt-2 tw-pb-2"
        [class.tw-pointer-events-none]="creating()"
        [class.tw-opacity-50]="creating()"
      >
        @for (cat of filtered(); track cat) {
          <button
            type="button"
            role="option"
            [attr.aria-selected]="cat === selectedName()"
            class="tw-flex tw-min-h-[48px] tw-mx-1 tw-shrink-0 tw-items-center tw-justify-between tw-gap-3 tw-rounded-xl tw-border-0 tw-bg-dt-surface-card tw-px-4 tw-py-2 tw-shadow-neuomorphic-raised-strong tw-cursor-pointer"
            (click)="selectCategory(cat)"
          >
            <span
              class="tw-min-w-0 tw-break-words tw-line-clamp-2 tw-text-left tw-text-[14px] tw-text-dt-text-headings"
              >{{ cat }}</span
            >
            <span class="tw-shrink-0 tw-text-[28px] tw-leading-none tw-text-neutral-800">+</span>
          </button>
        } @empty {
          <div class="tw-flex tw-items-center tw-justify-between tw-px-2 tw-py-1">
            <span class="tw-text-[14px] tw-text-dt-text-tertiary">{{
              "noCategoriesFound" | i18n
            }}</span>
            <button
              type="button"
              class="tw-cursor-pointer tw-border-0 tw-bg-transparent tw-text-[13px] tw-font-medium tw-text-dt-text-action"
              (click)="startCreate()"
            >
              {{ "createNewCategory" | i18n }}
            </button>
          </div>
        }
      </div>
    }
  </div>

  @if (searchTerm() && !selectedName() && !creating()) {
    <span class="tw-px-2 tw-text-[12px] tw-text-danger">{{ "pleaseSelectACategory" | i18n }}</span>
  }
</div>
